import type { Vendeur } from '@troc/shared'
import { eur } from '@troc/shared'
import { BOUTIQUE } from '../impression/documents'

/**
 * Le ticket tel qu'il sortira de l'imprimante, mis à jour à chaque article.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Pourquoi le montrer avant d'imprimer
 * ────────────────────────────────────────────────────────────────────────────
 *
 * La liste d'encaissement est faite pour agir : on y augmente une quantité, on
 * y corrige un prix. Elle ne dit pas ce que le client recevra. Or c'est sur le
 * papier que les erreurs se voient — une désignation qui déborde, un article
 * compté deux fois, un rendu incohérent — et il est trop tard une fois le
 * ticket coupé.
 *
 * L'aperçu reprend donc **la mise en page réelle** du ticket, à la largeur
 * réelle du rouleau, et non un résumé arrangé pour l'écran : un aperçu plus
 * joli que la sortie ne servirait à rien.
 *
 * Ce qui n'est pas encore connu — numéro de ticket, mode de paiement, rendu de
 * monnaie — s'affiche en attente plutôt que d'être inventé.
 */

export interface LigneApercu {
  ref: string
  libelle: string
  quantite: number
  prixCentimes: number
}

export function ApercuTicket({
  lignes,
  total,
  vendeur,
  ticketNo,
  encaisseCentimes,
  mode,
}: {
  lignes: LigneApercu[]
  total: number
  /** Choisi à l'encaissement : inconnu tant que la vente n'est pas payée. */
  vendeur?: Vendeur | null
  ticketNo?: number
  encaisseCentimes?: number
  mode?: 'especes' | 'cb' | 'mixte'
}) {
  const maintenant = new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date())

  const rendu =
    encaisseCentimes !== undefined && encaisseCentimes > total ? encaisseCentimes - total : 0

  return (
    <div className="apercu-rouleau">
      <div className="apercu-ticket">
        <div className="apercu-entete">
          <div className="marque">{BOUTIQUE.nom}</div>
          <div className="petit">
            {BOUTIQUE.adresse} · {BOUTIQUE.localite}
          </div>
          <div className="petit">
            {BOUTIQUE.tel} · {BOUTIQUE.tva}
          </div>
        </div>

        <div className="petit apercu-meta">
          Ticket n° {ticketNo ?? '—'} · {maintenant}
          <br />
          Vendeur : {vendeur?.nom ?? <span className="attente">à choisir</span>}
        </div>

        <table>
          <tbody>
            {lignes.map((l, i) => (
              <tr key={i}>
                <td>
                  {l.libelle}
                  <div className="ref">
                    {l.ref}
                    {l.quantite > 1 && ` · ${l.quantite} × ${eur(l.prixCentimes)}`}
                  </div>
                </td>
                <td className="droite titre">{eur(l.prixCentimes * l.quantite)}</td>
              </tr>
            ))}
            {lignes.length === 0 && (
              <tr>
                <td colSpan={2} className="attente" style={{ textAlign: 'center', padding: '14px 0' }}>
                  Aucun article
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="apercu-total">
          <div className="ligne grand titre">
            <span>TOTAL</span>
            <span>{eur(total)}</span>
          </div>
          {mode && (
            <div className="ligne">
              <span>{mode === 'cb' ? 'Carte bancaire' : mode === 'mixte' ? 'Mixte' : 'Espèces'}</span>
              <span>{encaisseCentimes !== undefined ? eur(encaisseCentimes) : eur(total)}</span>
            </div>
          )}
          {rendu > 0 && (
            <div className="ligne titre">
              <span>Rendu</span>
              <span>{eur(rendu)}</span>
            </div>
          )}
        </div>

        <div className="apercu-pied petit">
          Merci de votre visite.
          <br />
          Les articles vendus ne sont ni repris ni échangés.
        </div>
      </div>
    </div>
  )
}
